import type {
    ComponentStageData,
    ComponentStageHeuristicDefinition,
    PluginContext,
    ReadOnlyExtensionAPI,
    VirtualStructureEntryItem
} from '../../../types'
import {
    getParent,
    isBoxPlugin,
    isChildOfHeader,
    isHeaderContainer,
    isHeaderFirstRowChild,
    isInternalBox,
    isMenuContainerChild,
    isPinned,
    isPinnedToContainer,
    isPlugin,
    isSection,
    isSuperGrid,
    isSuperGridCell,
    isSectionsRootContainer,
    isSuperGridCellRootContainer,
    isStackContainerLayout,
    isCssGrid,
    shouldBeVerticallyStretched,
    updateTargetLayout
} from '../../common'
import {updateComponentVerticalMargins} from '../common'
import {
    getMeasurementsPointer,
    getSourceLayoutPointer,
    getStructurePointer,
    getTargetLayoutPointer,
    getVirtualStructurePointer
} from '../../../conversionDal/utils/pointer'
import {
    BOTTOM_PADDING,
    FIT_TO_SCREEN,
    HEADER_COMPONENTS_HEIGHT,
    HeightType,
    LIST_ITEM_PADDING_TOP,
    SECTION_MARGIN_CLAMP,
    TOP_BOTTOM_RELATION,
    TOP_PADDING
} from '../constants'
import {clamp} from '../../../utils/mathUtils'
import {SemanticTypes} from '../../../preprocess/conversionDataBuilders'
import _ from 'lodash'
import type {HorizontalScaleAndSizePluginAPI} from '../../horizontalScaleAndSize/types'
import {VirtualStructureTag} from '../../../enums'
import {deepClone} from '@wix/wix-immutable-proxy'
import {pointerUtils} from '@wix/document-manager-core'
import {VIEW_MODES} from '../../../../../constants/constants'
import type {ComponentPadding} from '@wix/viewer-manager-interface'
import {ReportableError} from '@wix/document-manager-utils'

const updatePinnedVerticalMargins = (componentStageData: ComponentStageData) => {
    const {conversionDal, componentPointer} = componentStageData
    const {itemLayout} = conversionDal.get(getSourceLayoutPointer(componentPointer))
    const {itemLayout: targetItemLayout} = conversionDal.get(getTargetLayoutPointer(componentPointer))
    const {margins, alignSelf, justifySelf} = itemLayout
    const {top, bottom} = margins

    updateTargetLayout(componentStageData, {
        itemLayout: _.merge({}, targetItemLayout, {
            alignSelf,
            justifySelf,
            margins: {
                top,
                bottom
            },
            type: 'FixedItemLayout'
        })
    })
}

//TODO: change this once we have a way heuristic to pinned containers
const updatePinnedToContainerVerticalMargins = (componentStageData: ComponentStageData) => {
    const {conversionDal, componentPointer} = componentStageData
    const {itemLayout} = conversionDal.get(getSourceLayoutPointer(componentPointer))
    const {itemLayout: targetItemLayout} = conversionDal.get(getTargetLayoutPointer(componentPointer))
    const {margins, alignSelf, justifySelf} = itemLayout
    const {top, bottom} = margins

    updateTargetLayout(componentStageData, {
        itemLayout: _.merge({}, targetItemLayout, {
            alignSelf,
            justifySelf,
            margins: {
                top,
                bottom
            },
            type: 'GridItemLayout'
        })
    })
}

const updateComponentHeight = (
    componentStageData: ComponentStageData,
    height: number,
    heightType: HeightType = HeightType.Fixed
) => {
    const {conversionDal, componentPointer} = componentStageData
    const {componentLayout} = conversionDal.get(getTargetLayoutPointer(componentPointer)) ?? {}

    if (heightType === HeightType.Auto) {
        updateTargetLayout(componentStageData, {
            componentLayout: {
                ...componentLayout,
                height: {
                    type: 'auto'
                },
                minHeight: {
                    value: height,
                    type: 'spx'
                },
                type: 'ComponentLayout'
            }
        })
    } else if (heightType === HeightType.AspectRatio) {
        updateTargetLayout(componentStageData, {
            componentLayout: {
                ...componentLayout,
                height: {
                    type: 'aspectRatio',
                    value: height
                },
                type: 'ComponentLayout'
            }
        })
    } else if (heightType === HeightType.Percentage) {
        updateTargetLayout(componentStageData, {
            componentLayout: {
                ...componentLayout,
                height: {
                    type: 'percentage',
                    value: height
                },
                type: 'ComponentLayout'
            }
        })
    } else {
        updateTargetLayout(componentStageData, {
            componentLayout: {
                ...componentLayout,
                height: {
                    value: height,
                    type: 'spx'
                },
                type: 'ComponentLayout'
            }
        })
    }
}

const resetVerticalPadding = (componentStageData: ComponentStageData) => {
    const {conversionDal, componentPointer} = componentStageData
    const {containerLayout} = deepClone(conversionDal.get(getTargetLayoutPointer(componentPointer))) ?? {}
    _.set(containerLayout, ['padding', 'top'], {value: 0, type: 'spx'})
    _.set(containerLayout, ['padding', 'bottom'], {value: 0, type: 'spx'})

    updateTargetLayout(componentStageData, {
        containerLayout
    })
}

const calculateAspectRatioHeight = (
    componentStageData: ComponentStageData,
    readOnlyExtensionAPI: ReadOnlyExtensionAPI
) => {
    const {componentPointer, initialSnapshot, globalContext} = componentStageData
    const componentMeasurementsPointer = getMeasurementsPointer(componentPointer)
    const originalMeasurements = initialSnapshot.getValue(componentMeasurementsPointer)
    const {horizontalScaleAndSize} = readOnlyExtensionAPI.getPluginsApi(globalContext)
    const width = (horizontalScaleAndSize as HorizontalScaleAndSizePluginAPI).getWidth(componentPointer)

    return Number(((originalMeasurements.height / originalMeasurements.width) * width).toFixed(2))
}

const setAspectRatioHeight = (componentStageData: ComponentStageData) => {
    const {componentPointer, initialSnapshot} = componentStageData
    const componentMeasurementsPointer = getMeasurementsPointer(componentPointer)
    const originalMeasurements = initialSnapshot.getValue(componentMeasurementsPointer)
    const heightToWidthRatio = originalMeasurements.height / originalMeasurements.width
    updateComponentHeight(componentStageData, heightToWidthRatio, HeightType.AspectRatio)
}

const setImageHeight = (
    componentStageData: ComponentStageData,
    readOnlyExtensionAPI: ReadOnlyExtensionAPI,
    {
        minHeight,
        maxHeight,
        keepAspectRatio
    }: {
        minHeight: number
        maxHeight: number
        keepAspectRatio?: boolean
    }
) => {
    const {initialSnapshot, componentPointer, globalContext} = componentStageData
    const componentMeasurementsPointer = getMeasurementsPointer(componentPointer)
    const {height} = initialSnapshot.getValue(componentMeasurementsPointer)
    const {horizontalScaleAndSize} = readOnlyExtensionAPI.getPluginsApi(globalContext)
    const width = (horizontalScaleAndSize as HorizontalScaleAndSizePluginAPI).getWidth(componentPointer)

    if (keepAspectRatio) {
        const aspectRatioHeight = calculateAspectRatioHeight(componentStageData, readOnlyExtensionAPI)
        const heightToWidthRatio = Math.max(Math.min(aspectRatioHeight, maxHeight), minHeight) / width
        return updateComponentHeight(componentStageData, heightToWidthRatio, HeightType.AspectRatio)
    }

    const heightToWidthRatio = Math.max(Math.min(height, maxHeight), minHeight) / width
    updateComponentHeight(componentStageData, heightToWidthRatio, HeightType.AspectRatio)
}

const setMeasuredHeight = (componentStageData: ComponentStageData) => {
    const {initialSnapshot, componentPointer} = componentStageData

    updateComponentHeight(componentStageData, initialSnapshot.getValue(getMeasurementsPointer(componentPointer)).height)
}

const getEffectiveSemanticType = (componentStageData: ComponentStageData, componentPointer: {id: string}): string => {
    const {conversionDal, initialSnapshot} = componentStageData
    const component = conversionDal.get(getStructurePointer(componentPointer))

    if (isStackContainerLayout({initialSnapshot, componentPointer: getStructurePointer(componentPointer)})) {
        const children = component.components
        if (children?.length) {
            return conversionDal.get(getStructurePointer({id: children[0]})).semanticType
        }
    }

    return component.semanticType
}

const getEffectivePreviousSemanticType = (
    componentStageData: ComponentStageData,
    previousSiblingPointer: {id: string}
): string => {
    const {conversionDal, initialSnapshot} = componentStageData
    const previousSibling = conversionDal.get(getStructurePointer(previousSiblingPointer))

    if (isStackContainerLayout({initialSnapshot, componentPointer: getStructurePointer(previousSiblingPointer)})) {
        const children = previousSibling.components
        if (children?.length) {
            return conversionDal.get(getStructurePointer({id: children[children.length - 1]})).semanticType
        }
    }

    return previousSibling.semanticType
}

const genericBetweenCompTypesPredicate =
    (lowerCompSemanticType: string, upperCompSemanticType: string) => (componentStageData: ComponentStageData) => {
        const {componentPointer, conversionDal} = componentStageData
        const component = conversionDal.get(componentPointer)
        const containerPointer = getStructurePointer({id: component.parent})
        const container = conversionDal.get(containerPointer)
        const currentCompIndex = container.components!.indexOf(componentPointer.id)
        const previousSiblingPointer = getStructurePointer({
            id: container.components![currentCompIndex - 1]
        })
        const previousSibling = conversionDal.get(previousSiblingPointer)

        if (!previousSibling) {
            throw new ReportableError({
                errorType: 'InvalidBuilderStructure',
                message: 'cannot process structure',
                extras: {
                    container,
                    component
                }
            })
        }

        const effectiveCurrentType = getEffectiveSemanticType(componentStageData, componentPointer)
        const effectivePreviousType = getEffectivePreviousSemanticType(componentStageData, previousSiblingPointer)

        return effectiveCurrentType === lowerCompSemanticType && effectivePreviousType === upperCompSemanticType
    }

const getGenericYHeuristic = (): ComponentStageHeuristicDefinition[] => {
    const heuristics = []

    for (const [lowerCompSemanticType, settings] of Object.entries(TOP_BOTTOM_RELATION)) {
        for (const [upperSemanticCompType, paddingValue] of Object.entries(settings)) {
            heuristics.push({
                predicate: genericBetweenCompTypesPredicate(lowerCompSemanticType, upperSemanticCompType),
                apply: (componentStageData: ComponentStageData) => {
                    updateComponentVerticalMargins(componentStageData, paddingValue)
                },
                heuristicCode: `${lowerCompSemanticType} TOP MARGIN FROM ${upperSemanticCompType}`
            })
        }
    }

    return heuristics
}

const stretchToContainerHeight = (componentStageData: ComponentStageData) => {
    updateComponentHeight(componentStageData, 100, HeightType.Percentage)
}

const setAutoHeightWithVhMinHeight = (componentStageData: ComponentStageData, minHeightVh: number) => {
    const {conversionDal, componentPointer} = componentStageData
    const {componentLayout} = conversionDal.get(getTargetLayoutPointer(componentPointer)) ?? {}

    updateTargetLayout(componentStageData, {
        componentLayout: {
            ...componentLayout,
            height: {type: 'auto'},
            minHeight: {type: 'vh', value: minHeightVh},
            type: 'ComponentLayout'
        }
    })
}

const getDesktopFitToScreenVh = (componentStageData: ComponentStageData): number | undefined => {
    const {conversionDal, componentPointer} = componentStageData
    const sourceLayout = conversionDal.get(getSourceLayoutPointer(componentPointer))
    const componentLayout = sourceLayout?.componentLayout

    if (!componentLayout) {
        return undefined
    }

    const {height, minHeight, maxHeight} = componentLayout

    if (minHeight?.type !== 'vh') {
        return undefined
    }

    if (maxHeight?.type !== 'px' || maxHeight.value !== 99999) {
        return undefined
    }

    if (height !== undefined && height?.type !== 'auto' && height?.type !== 'vh') {
        return undefined
    }

    const heightVh = height?.type === 'vh' ? height?.value : -Infinity
    return Math.max(heightVh, minHeight.value)
}

const updateComponentPadding = (componentStageData: ComponentStageData, padding: Partial<ComponentPadding>) => {
    const {conversionDal, componentPointer} = componentStageData
    updateTargetLayout(
        componentStageData,
        _.merge({}, conversionDal.get(getTargetLayoutPointer(componentPointer)), {
            containerLayout: {
                padding: Object.fromEntries(
                    Object.entries(padding).map(([key, value]) => [
                        key,
                        {
                            type: 'spx',
                            value
                        }
                    ])
                )
            }
        })
    )
}

const updateGridVerticalPadding = (componentStageData: ComponentStageData) => {
    const {conversionDal, componentPointer} = componentStageData
    const sourceLayout = conversionDal.get(getSourceLayoutPointer(componentPointer))
    const {containerLayout} = deepClone(conversionDal.get(getTargetLayoutPointer(componentPointer))) ?? {}

    const sourcePadding = sourceLayout?.containerLayout?.padding

    _.set(containerLayout, ['padding', 'top'], {
        value: sourcePadding?.top?.value ? 24 : 0,
        type: 'spx'
    })

    _.set(containerLayout, ['padding', 'bottom'], {
        value: sourcePadding?.bottom?.value ? 24 : 0,
        type: 'spx'
    })

    _.set(containerLayout, ['rowGap'], {
        value: sourceLayout?.containerLayout?.rowGap?.value ? 24 : 0,
        type: 'spx'
    })

    updateTargetLayout(componentStageData, {
        containerLayout
    })
}

export const heightHeuristics: ComponentStageHeuristicDefinition[] = [
    {
        predicate: ({conversionDal, componentPointer, initialSnapshot}: ComponentStageData) => {
            if (conversionDal.get(componentPointer).semanticType !== SemanticTypes.IMAGE) {
                return false
            }

            const parentPointer = getParent(conversionDal, componentPointer)

            if (!parentPointer) {
                return false
            }

            const originalMeasurements = initialSnapshot.getValue(getMeasurementsPointer(componentPointer))
            const parentOriginalMeasurements = initialSnapshot.getValue(getMeasurementsPointer(parentPointer))

            return (
                Math.abs(originalMeasurements.width - parentOriginalMeasurements.width) <= 6 &&
                isSectionsRootContainer({conversionDal, componentPointer: parentPointer!})
            )
        },
        apply: (
            componentStageData: ComponentStageData,
            contextHelper: PluginContext,
            readOnlyExtensionAPI: ReadOnlyExtensionAPI
        ) => {
            setImageHeight(componentStageData, readOnlyExtensionAPI, {
                minHeight: 400,
                maxHeight: 400,
                keepAspectRatio: false
            })
        },
        heuristicCode: 'IMAGE WIDTH IS THE APPROXIMATELY THE SAME SIZE OF THE PARENT SECTION'
    },
    {
        predicate: ({componentPointer, conversionDal, initialSnapshot}: ComponentStageData) => {
            const componentMeasurementsPointer = getMeasurementsPointer(componentPointer)
            const {width} = initialSnapshot.getValue(componentMeasurementsPointer)
            const component = conversionDal.get(componentPointer)

            return component.semanticType === SemanticTypes.IMAGE && width < 201
        },
        apply: (
            componentStageData: ComponentStageData,
            _contextHelper: PluginContext,
            readOnlyExtensionAPI: ReadOnlyExtensionAPI
        ) =>
            setImageHeight(componentStageData, readOnlyExtensionAPI, {
                minHeight: 48,
                maxHeight: 600
            }),
        heuristicCode: 'NARROW IMAGE HEIGHT'
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            const component = conversionDal.get(componentPointer)

            return component.semanticType === SemanticTypes.IMAGE
        },
        apply: (
            componentStageData: ComponentStageData,
            _contextHelper: PluginContext,
            readOnlyExtensionAPI: ReadOnlyExtensionAPI
        ) =>
            setImageHeight(componentStageData, readOnlyExtensionAPI, {
                minHeight: 48,
                maxHeight: 300,
                keepAspectRatio: true
            }),
        heuristicCode: 'IMAGE FALLBACK HEIGHT'
    },
    {
        predicate: (
            {componentPointer, conversionDal}: ComponentStageData,
            _contextHelper: PluginContext,
            readOnlyExtensionAPI: ReadOnlyExtensionAPI
        ) => {
            const component = conversionDal.get(componentPointer)

            return (
                component.semanticType === SemanticTypes.SVG ||
                component.semanticType === SemanticTypes.LOGO ||
                readOnlyExtensionAPI.layout.shouldPreserveAspectRatio(
                    pointerUtils.getCompPointer(componentPointer.id, VIEW_MODES.DESKTOP)
                )
            )
        },
        apply: (componentStageData: ComponentStageData) => {
            const {conversionDal, componentPointer} = componentStageData
            const {componentLayout} = conversionDal.get(getSourceLayoutPointer(componentPointer))
            if (componentLayout?.height?.type === 'aspectRatio') {
                updateComponentHeight(componentStageData, componentLayout.height.value, HeightType.AspectRatio)
            } else {
                setAspectRatioHeight(componentStageData)
            }
        },
        heuristicCode: 'ASPECT RATIO HEIGHT'
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            return isChildOfHeader(componentPointer, conversionDal)
        },
        apply: (componentStageData: ComponentStageData) =>
            updateComponentHeight(componentStageData, 0, HeightType.Auto),
        heuristicCode: 'COMPONENT IN HEADER'
    },
    {
        predicate: isSuperGrid,
        apply: () => {},
        heuristicCode: 'SUPERGRID AUTO HEIGHT'
    },
    {
        predicate: (componentStageData: ComponentStageData) => {
            if (!isSection(componentStageData)) {
                return false
            }
            const effectiveVh = getDesktopFitToScreenVh(componentStageData)
            return effectiveVh !== undefined && effectiveVh >= FIT_TO_SCREEN.VH_THRESHOLD
        },
        apply: (componentStageData: ComponentStageData) =>
            setAutoHeightWithVhMinHeight(componentStageData, FIT_TO_SCREEN.FULL_VH),
        heuristicCode: 'SECTION FIT TO SCREEN >= 80VH'
    },
    {
        predicate: (componentStageData: ComponentStageData) => {
            if (!isSection(componentStageData)) {
                return false
            }
            const effectiveVh = getDesktopFitToScreenVh(componentStageData)
            return effectiveVh !== undefined && effectiveVh < FIT_TO_SCREEN.VH_THRESHOLD
        },
        apply: (componentStageData: ComponentStageData) => {
            const effectiveVh = getDesktopFitToScreenVh(componentStageData)!
            setAutoHeightWithVhMinHeight(componentStageData, effectiveVh)
        },
        heuristicCode: 'SECTION FIT TO SCREEN < 80VH'
    },
    {
        predicate: (componentStageData: ComponentStageData) => isSection(componentStageData),
        apply: () => {},
        heuristicCode: 'SECTION AUTO HEIGHT'
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) =>
            isHeaderContainer({componentPointer, conversionDal}),
        apply: () => {},
        heuristicCode: 'HEADER CONTAINER'
    },
    {
        predicate: ({initialSnapshot, componentPointer}: ComponentStageData) =>
            isStackContainerLayout({initialSnapshot, componentPointer}),
        apply: (componentStageData: ComponentStageData) =>
            updateComponentHeight(componentStageData, 0, HeightType.Auto),
        heuristicCode: 'STACK CONTAINER AUTO HEIGHT'
    },
    {
        predicate: (componentStageData: ComponentStageData) =>
            isInternalBox(componentStageData) || isBoxPlugin(componentStageData),
        apply: stretchToContainerHeight,
        heuristicCode: 'STRETCH INTERNAL BOX'
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            const component = conversionDal.get(componentPointer)

            return component.semanticType === SemanticTypes.BUTTON
        },
        apply: setMeasuredHeight,
        heuristicCode: 'BUTTON MEASURED HEIGHT'
    },
    {
        predicate: (
            {componentPointer}: ComponentStageData,
            _contextHelper?: PluginContext,
            readOnlyExtensionApi?: ReadOnlyExtensionAPI
        ) =>
            !!readOnlyExtensionApi?.layout.isContentHeight(
                pointerUtils.getCompPointer(componentPointer.id, VIEW_MODES.DESKTOP)
            ),
        apply: (componentStageData: ComponentStageData) => updateComponentHeight(componentStageData, 0, HeightType.Auto)
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            const component = conversionDal.get(componentPointer)

            return component.semanticType === SemanticTypes.EMBEDDED_MEDIA
        },
        apply: (
            componentStageData: ComponentStageData,
            _contextHelper: PluginContext,
            readOnlyExtensionAPI: ReadOnlyExtensionAPI
        ) =>
            updateComponentHeight(
                componentStageData,
                calculateAspectRatioHeight(componentStageData, readOnlyExtensionAPI)
            ),
        heuristicCode: 'ABSOLUTE ASPECT RATIO HEIGHT'
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            const component = conversionDal.get(componentPointer)

            return component.semanticType === SemanticTypes.TEXT
        },
        apply: (componentStageData: ComponentStageData) =>
            updateComponentHeight(componentStageData, 0, HeightType.Auto),
        heuristicCode: 'TEXT FALLBACK HEIGHT'
    },
    {
        predicate: (componentStageData: ComponentStageData) => {
            if (!isPlugin(componentStageData)) {
                return false
            }
            const {conversionDal, componentPointer} = componentStageData
            const sourceLayout = conversionDal.get(getSourceLayoutPointer(componentPointer))
            const heightType = sourceLayout?.componentLayout?.height?.type
            return heightType === 'spx' || heightType === 'px'
        },
        apply: (componentStageData: ComponentStageData) => {
            const {conversionDal, componentPointer} = componentStageData
            const sourceLayout = conversionDal.get(getSourceLayoutPointer(componentPointer))
            updateComponentHeight(componentStageData, sourceLayout.componentLayout.height.value)
        },
        heuristicCode: 'PLUGIN FALLBACK HEIGHT'
    },
    {
        predicate: (componentStageData: ComponentStageData) => isPlugin(componentStageData),
        apply: _.noop,
        heuristicCode: 'PLUGIN NOOP'
    },
    {
        predicate: shouldBeVerticallyStretched,
        apply: stretchToContainerHeight,
        heuristicCode: 'KEEP VERTICALLY STRETCHED HEIGHT'
    },
    {
        predicate: () => true,
        apply: setMeasuredHeight,
        heuristicCode: 'ANY COMPONENT FALLBACK HEIGHT'
    }
]

export const verticalSpacingHeuristics: ComponentStageHeuristicDefinition[] = [
    {
        predicate: (componentStageData: ComponentStageData) => isSection(componentStageData),
        apply: () => {}
    },
    {
        predicate: (componentStageData: ComponentStageData) => isPinned(componentStageData),
        apply: (componentStageData: ComponentStageData) => {
            //TODO: change this once we have a way heuristic to pinned containers
            if (isPinnedToContainer(componentStageData)) {
                updatePinnedToContainerVerticalMargins(componentStageData)
                return
            }
            updatePinnedVerticalMargins(componentStageData)
        },
        heuristicCode: 'Pinned Element'
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) =>
            isMenuContainerChild(componentPointer, conversionDal),
        apply: (componentStageData: ComponentStageData) => {
            updateComponentVerticalMargins(componentStageData, HEADER_COMPONENTS_HEIGHT.COMPONENT_IN_MENU_TOP_MARGIN)
        },
        heuristicCode: 'MENU CONTAINER CHILD'
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            return isHeaderFirstRowChild(componentPointer, conversionDal)
        },
        apply: (componentStageData: ComponentStageData) => {
            updateComponentVerticalMargins(componentStageData, 0)
        },
        heuristicCode: 'COMPONENT IN HEADER FIRST ROW'
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            return isChildOfHeader(componentPointer, conversionDal)
        },
        apply: (componentStageData: ComponentStageData) => {
            updateComponentVerticalMargins(componentStageData, HEADER_COMPONENTS_HEIGHT.COMPONENT_IN_HEADER_TOP_MARGIN)
        },
        heuristicCode: 'COMPONENT IN HEADER'
    },
    {
        predicate: isSuperGrid,
        apply: updateGridVerticalPadding,
        heuristicCode: 'SUPERGRID VERTICAL PADDING AND ROW GAP'
    },
    {
        predicate: isCssGrid,
        apply: updateGridVerticalPadding,
        heuristicCode: 'CSSGRID VERTICAL PADDING AND ROW GAP'
    },
    {
        predicate: isSuperGridCell,
        apply: (componentStageData: ComponentStageData) => {
            updateComponentVerticalMargins(componentStageData, 0, 0, true)
        },
        heuristicCode: '0 VERTICAL MARGINS FOR SUPERGRID CELL'
    },
    {
        predicate: isSuperGridCellRootContainer,
        apply: (componentStageData: ComponentStageData) => {
            updateComponentPadding(componentStageData, {
                bottom: BOTTOM_PADDING.CELL
            })
        },
        heuristicCode: 'KEEP PADDING FOR SUPERGRID CELL'
    },
    {
        predicate: (componentStageData: ComponentStageData) =>
            isInternalBox(componentStageData) || isBoxPlugin(componentStageData),
        apply: resetVerticalPadding,
        heuristicCode: 'RESET VERTICAL PADDING FOR INTERNAL BOX'
    },
    {
        predicate: (componentStageData: ComponentStageData) => isPlugin(componentStageData),
        apply: _.noop
    },
    {
        predicate: (componentStageData: ComponentStageData) => {
            const {componentPointer, conversionDal} = componentStageData
            const component = conversionDal.get(componentPointer)
            const containerPointer = getStructurePointer({
                id: component.parent
            })
            const container = conversionDal.get(containerPointer)
            const currentCompIndex = container.components!.indexOf(componentPointer.id)

            return (
                isSectionsRootContainer({
                    componentPointer: containerPointer,
                    conversionDal
                }) && currentCompIndex === 0
            )
        },
        apply: (componentStageData: ComponentStageData) => {
            const {initialSnapshot, componentPointer} = componentStageData
            const measurements = initialSnapshot.getValue(getMeasurementsPointer(componentPointer))
            const topMargin = clamp(
                measurements?.y ?? TOP_PADDING.ROOT,
                SECTION_MARGIN_CLAMP.MIN,
                SECTION_MARGIN_CLAMP.MAX
            )
            updateComponentVerticalMargins(componentStageData, topMargin)
        },
        heuristicCode: 'FIRST CHILD IN SECTION MARGINS'
    },
    {
        predicate: (componentStageData: ComponentStageData) => {
            const {componentPointer, conversionDal} = componentStageData
            const component = conversionDal.get(componentPointer)
            const containerPointer = getStructurePointer({
                id: component.parent
            })
            const container = conversionDal.get(containerPointer)
            const currentCompIndex = container.components!.indexOf(componentPointer.id)

            return (
                currentCompIndex === 0 &&
                (isInternalBox({
                    componentPointer: containerPointer,
                    conversionDal
                }) ||
                    isBoxPlugin({
                        componentPointer: containerPointer,
                        conversionDal
                    }))
            )
        },
        apply: (componentStageData: ComponentStageData) => {
            updateComponentVerticalMargins(componentStageData, TOP_PADDING.DESCENDANT)
        },
        heuristicCode: 'FIRST CHILD IN BOX MARGINS'
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            const component = conversionDal.get(componentPointer)
            const virtualParent = conversionDal.get(
                getVirtualStructurePointer({id: component.virtualParentId})
            ) as VirtualStructureEntryItem

            return (
                virtualParent &&
                virtualParent.tag === VirtualStructureTag.Virtual &&
                virtualParent.components!.indexOf(componentPointer.id) === 0
            )
        },
        apply: (componentStageData: ComponentStageData) => {
            updateComponentVerticalMargins(componentStageData, LIST_ITEM_PADDING_TOP)
        },
        heuristicCode: 'FIRST CHILD IN VIRTUAL LIST ITEM MARGINS'
    },
    ...getGenericYHeuristic(),
    {
        predicate: shouldBeVerticallyStretched,
        apply: (componentStageData: ComponentStageData) => {
            updateComponentVerticalMargins(componentStageData, 0, 0, true)
        },
        heuristicCode: 'KEEP VERTICALLY STRETCHED MARGINS'
    },
    {
        predicate: () => true,
        apply: (componentStageData: ComponentStageData) => {
            updateComponentVerticalMargins(componentStageData, TOP_PADDING.ROOT)
        },
        heuristicCode: 'DEFAULT MARGINS'
    }
]
